import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FileText, History, Loader2, Variable } from 'lucide-react'
import { formatChangeLogCreatedAt } from '@/lib/utils/formatChangeLogCreatedAt'
import { DatePicker } from '@/components/ui/DatePicker'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import {
  TermsDeclarationTemplatesGroup,
  TermsDocumentTemplateBlock,
} from '../components/TermsDocumentTemplateBlock'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import cardStyles from '../styles/documentCard.module.css'
import { ChangeReasonModal } from '../components/ChangeReasonModal'
import {
  getPolicyDeclarationById,
  patchPolicyDeclaration,
  savePolicyDeclaration,
} from '../services/policyDeclarationService'
import type { PolicyDeclarationItem, PolicyDeclarationSubmitPayload } from '../types/policyDeclaration'
import {
  TERMS_SECTION1_AVAILABLE_VARIABLES,
  TERMS_SECTION2_AVAILABLE_VARIABLES,
} from '../constants/termsConditionsEdit'
import { asTrimmedString } from '../utils/coerceStrings'
import {
  isEndDateBeforeStart,
  toDatePickerMinDate,
} from '../utils/effectiveDateRange'
import { END_DATE_BEFORE_START_MESSAGE } from '../constants/effectiveDateRange'

type EditState = {
  policy_id: string
  template_name: string
  policy_start_date: string
  policy_end_date: string
  policy_template: string
  declaration_template: string
  declaration_resident_template: string
}

type EditorDirtyFlags = {
  policy: boolean
  declaration: boolean
  resident: boolean
}

function normalizeDate(value: unknown): string {
  if (value == null || value === '') return ''
  const str = asTrimmedString(value)
  if (!str) return ''
  const isoPrefix = str.match(/^(\d{4}-\d{2}-\d{2})/)
  if (isoPrefix) return isoPrefix[1]
  return ''
}

function endOfYear(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-\d{2}-\d{2}$/)
  return match ? `${match[1]}-12-31` : ''
}

/** Angular policy-declaration history: `date:'MM/dd/yy hh:mm:ss a'`. */
function formatHistoryTimestamp(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'number') {
    return formatChangeLogCreatedAt(value, 'MM/dd/yy hh:mm:ss a')
  }
  const raw = asTrimmedString(value)
  if (!raw) return ''
  return formatChangeLogCreatedAt(raw, 'MM/dd/yy hh:mm:ss a')
}

const EMPTY_STATE: EditState = {
  policy_id: '',
  template_name: '',
  policy_start_date: '',
  policy_end_date: '',
  policy_template: '',
  declaration_template: '',
  declaration_resident_template: '',
}



function AvailableVariablesPanel({
  variables,
  headingId,
}: {
  variables: readonly string[]
  headingId: string
}) {
  return (
    <div className={cardStyles.variablesBox}>
      <p className="mb-2 block text-sm font-extrabold tracking-wide text-slate-900" id={headingId}>
        Available Variables:
      </p>
      <div
        className="mt-2 flex flex-wrap gap-x-2 gap-y-1.5"
        role="list"
        aria-labelledby={headingId}
      >
        {variables.map((v) => (
          <code key={v} className={cardStyles.variableCode}>
            {v}
          </code>
        ))}
      </div>
    </div>
  )
}

export function TermsConditionsEditPage() {
  const { policyId } = useParams<{ policyId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [draftSaving, setDraftSaving] = useState(false)
  const [reasonOpen, setReasonOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<'section1' | 'section2'>('section1')
  const [form, setForm] = useState<EditState>(EMPTY_STATE)
  const [errors, setErrors] = useState<Partial<Record<keyof EditState, string>>>({})
  const [policyDetails, setPolicyDetails] = useState<PolicyDeclarationItem | null>(null)
  const [editorDirty, setEditorDirty] = useState<EditorDirtyFlags>({
    policy: false,
    declaration: false,
    resident: false,
  })

  const initialRef = useRef<EditState>(EMPTY_STATE)
  const formRef = useRef(form)
  const editorDirtyRef = useRef(editorDirty)
  const draftInFlightRef = useRef(false)
  formRef.current = form
  editorDirtyRef.current = editorDirty

  const numericId = Number(policyId)

  useEffect(() => {
    if (!numericId) {
      navigate('/terms-and-conditions', { replace: true })
      return
    }

    let active = true
    const load = async () => {
      setLoading(true)
      try {
        const detail = await getPolicyDeclarationById(numericId)
        const policyTpl = detail.policy_template_draft ?? detail.policy_template ?? ''
        const declTpl = detail.declaration_template_draft ?? detail.declaration_template ?? ''
        const declResTpl =
          detail.declaration_resident_template_draft ?? detail.declaration_resident_template ?? ''

        const next: EditState = {
          policy_id: asTrimmedString(detail.policy_id),
          template_name: asTrimmedString(detail.template_name),
          policy_start_date: normalizeDate(detail.policy_start_date),
          policy_end_date: normalizeDate(detail.policy_end_date),
          policy_template: asTrimmedString(policyTpl),
          declaration_template: asTrimmedString(declTpl),
          declaration_resident_template: asTrimmedString(declResTpl),
        }
        if (!active) return
        setPolicyDetails(detail)
        setForm(next)
        initialRef.current = next
        setEditorDirty({ policy: false, declaration: false, resident: false })
      } catch (error) {
        showApiError(error)
      } finally {
        if (active) setLoading(false)
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [numericId, navigate])

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialRef.current),
    [form],
  )

  const updateField = <K extends keyof EditState>(key: K, value: EditState[K]) => {
    setForm((current) => ({ ...current, [key]: value }))
    setErrors((current) => {
      if (!current[key]) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }

  const flushDraftsFromRefs = useCallback(async () => {
    if (!numericId || draftInFlightRef.current) return
    const f = formRef.current
    const d = editorDirtyRef.current
    const payload: Record<string, string> = {}
    if (d.policy) payload.policy_template_draft = f.policy_template
    if (d.declaration) payload.declaration_template_draft = f.declaration_template
    if (d.resident) payload.declaration_resident_template_draft = f.declaration_resident_template
    if (Object.keys(payload).length === 0) return

    draftInFlightRef.current = true
    setDraftSaving(true)
    try {
      await patchPolicyDeclaration(numericId, payload)
      setPolicyDetails((prev) => (prev ? { ...prev, ...payload } : prev))
      setEditorDirty({ policy: false, declaration: false, resident: false })
      showSuccess('Terms & Conditions Draft Save Successfully')
    } catch (error) {
      showApiError(error)
    } finally {
      draftInFlightRef.current = false
      setDraftSaving(false)
    }
  }, [numericId])

  /** Angular `startTimer` → `updateDraft` every 180s when any editor is dirty. */
  useEffect(() => {
    if (!numericId) return
    const timer = window.setInterval(() => {
      void flushDraftsFromRefs()
    }, 180_000)
    return () => window.clearInterval(timer)
  }, [numericId, flushDraftsFromRefs])

  const saveDraftPolicy = async () => {
    if (!numericId || !editorDirty.policy) return
    await flushDraftsFromRefs()
  }

  const saveDraftDeclaration = async () => {
    if (!numericId || !editorDirty.declaration) return
    await flushDraftsFromRefs()
  }

  const saveDraftResident = async () => {
    if (!numericId || !editorDirty.resident) return
    await flushDraftsFromRefs()
  }

  const discardDraft = async (type: 'policy_template' | 'declaration_template' | 'declaration_resident_template') => {
    if (!numericId || draftInFlightRef.current) return
    draftInFlightRef.current = true
    setDraftSaving(true)
    try {
      if (type === 'policy_template') {
        await patchPolicyDeclaration(numericId, { policy_template_draft: '' })
        const published = policyDetails?.policy_template ?? ''
        updateField('policy_template', published)
        setPolicyDetails((prev) => (prev ? { ...prev, policy_template_draft: null } : prev))
        setEditorDirty((x) => ({ ...x, policy: false }))
      } else if (type === 'declaration_template') {
        await patchPolicyDeclaration(numericId, { declaration_template_draft: '' })
        const published = policyDetails?.declaration_template ?? ''
        updateField('declaration_template', published)
        setPolicyDetails((prev) => (prev ? { ...prev, declaration_template_draft: null } : prev))
        setEditorDirty((x) => ({ ...x, declaration: false }))
      } else {
        await patchPolicyDeclaration(numericId, { declaration_resident_template_draft: '' })
        const published = policyDetails?.declaration_resident_template ?? ''
        updateField('declaration_resident_template', published)
        setPolicyDetails((prev) =>
          prev ? { ...prev, declaration_resident_template_draft: null } : prev,
        )
        setEditorDirty((x) => ({ ...x, resident: false }))
      }
      showSuccess('Draft Discard Successfully')
    } catch (error) {
      showApiError(error)
    } finally {
      draftInFlightRef.current = false
      setDraftSaving(false)
    }
  }

  const validate = (): boolean => {
    const nextErrors: Partial<Record<keyof EditState, string>> = {}
    if (!asTrimmedString(form.policy_id)) nextErrors.policy_id = 'Document ID is required.'
    if (!asTrimmedString(form.template_name)) nextErrors.template_name = 'Filename is required.'
    if (!form.policy_start_date) nextErrors.policy_start_date = 'Start Date is required.'
    if (!form.policy_end_date) nextErrors.policy_end_date = 'End Date is required.'
    if (isEndDateBeforeStart(form.policy_start_date, form.policy_end_date)) {
      nextErrors.policy_end_date = END_DATE_BEFORE_START_MESSAGE
    }
    if (!asTrimmedString(form.policy_template)) nextErrors.policy_template = 'Document template is required.'
    if (!asTrimmedString(form.declaration_template)) {
      nextErrors.declaration_template = 'Declaration template is required.'
    }
    if (!asTrimmedString(form.declaration_resident_template)) {
      nextErrors.declaration_resident_template = 'Declaration resident template is required.'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const doSave = async (reason?: string) => {
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload: PolicyDeclarationSubmitPayload = {
        policy_id: asTrimmedString(form.policy_id),
        template_name: asTrimmedString(form.template_name),
        policy_start_date: form.policy_start_date,
        policy_end_date: form.policy_end_date,
        policy_template: form.policy_template,
        declaration_template: form.declaration_template,
        declaration_resident_template: form.declaration_resident_template,
        ...(reason ? { change_reason: reason } : {}),
      }
      await savePolicyDeclaration(payload, numericId)
      showSuccess('Terms & Conditions Added Successfully!')
      initialRef.current = form
      navigate('/terms-and-conditions')
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    if (!isDirty) return
    if (!validate()) return
    setReasonOpen(true)
  }

  const fieldClassName = cardStyles.customFormControl

  const hasPolicyDraft = Boolean(policyDetails?.policy_template_draft)
  const hasDeclarationDraft = Boolean(policyDetails?.declaration_template_draft)
  const hasResidentDraft = Boolean(policyDetails?.declaration_resident_template_draft)

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-[#f4f7f6]">
        <Loader2 className="h-10 w-10 animate-spin text-[#1597ff]" aria-label="Loading" />
      </div>
    )
  }

  return (
    <div className="min-h-screen w-full bg-[#f4f7f6] pb-12 font-sans text-slate-900">
      <PageHeader title="EDIT TERMS & CONDITIONS" testIdRoot="terms-conditions-edit-page-header" />
      <div className="mx-auto flex w-full flex-col gap-6 px-3 py-6 sm:px-6">
        <div className="overflow-visible rounded-xl border border-slate-200/90 bg-white shadow-[0_5px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-100">
          <div className="border-b border-slate-200 px-2 pb-0 sm:px-6">
            <nav
              className="grid min-w-0 grid-cols-2"
              aria-label="Terms and conditions sections"
              role="tablist"
            >
              <button
                type="button"
                onClick={() => setActiveSection('section1')}
                className={cn(
                  'w-full border-b-2 px-2.5 py-3 text-center text-xs font-medium transition-colors sm:px-4 sm:py-4 sm:text-sm',
                  activeSection === 'section1'
                    ? 'border-[#1597ff] text-[#1597ff]'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
                )}
                role="tab"
                aria-selected={activeSection === 'section1'}
              >
                Section 1
              </button>
              <button
                type="button"
                onClick={() => setActiveSection('section2')}
                className={cn(
                  'w-full border-b-2 px-2.5 py-3 text-center text-xs font-medium transition-colors sm:px-4 sm:py-4 sm:text-sm',
                  activeSection === 'section2'
                    ? 'border-[#1597ff] text-[#1597ff]'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
                )}
                role="tab"
                aria-selected={activeSection === 'section2'}
              >
                Section 2
              </button>
            </nav>
          </div>

          <div className="px-4 py-6 sm:px-6 space-y-5">
              {activeSection === 'section1' ? (
                <>
                  {/* Section 1 Fields Card */}
                  <div className={cardStyles.card}>
                    <div className={cardStyles.cardHeader}>
                      <FileText className={cardStyles.cardIcon} />
                      <h3 className={cardStyles.cardTitle}>Document Details</h3>
                    </div>
                    <div className={cardStyles.cardBody}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <div className={cardStyles.formGroup}>
                          <label className={cardStyles.formLabel}>
                            Document ID
                          </label>
                          <input
                            type="text"
                            value={form.policy_id}
                            onChange={(event) => updateField('policy_id', event.target.value)}
                            className={fieldClassName}
                          />
                          {errors.policy_id ? (
                            <p className={cardStyles.errorText}>{errors.policy_id}</p>
                          ) : null}
                        </div>
                        <div className={cardStyles.formGroup}>
                          <label className={cardStyles.formLabel}>
                            Filename
                          </label>
                          <input
                            type="text"
                            value={form.template_name}
                            onChange={(event) => updateField('template_name', event.target.value)}
                            className={fieldClassName}
                          />
                          {errors.template_name ? (
                            <p className={cardStyles.errorText}>{errors.template_name}</p>
                          ) : null}
                        </div>
                        <div className={cardStyles.formGroup}>
                          <label className={cardStyles.formLabel}>
                            Effective Start Date
                          </label>
                          <DatePicker
                            value={form.policy_start_date}
                            onChange={(value) => {
                              updateField('policy_start_date', value)
                              if (value && !form.policy_end_date) {
                                updateField('policy_end_date', endOfYear(value))
                              }
                            }}
                            placeholder="MM/DD/YYYY"
                            allowFutureDates={true}
                            showHelperText={false}
                            className={fieldClassName}
                          />
                          {errors.policy_start_date ? (
                            <p className={cardStyles.errorText}>{errors.policy_start_date}</p>
                          ) : null}
                        </div>
                        <div className={cardStyles.formGroup}>
                          <label className={cardStyles.formLabel}>
                            Effective End Date
                          </label>
                          <DatePicker
                            value={form.policy_end_date}
                            onChange={(value) => updateField('policy_end_date', value)}
                            placeholder="MM/DD/YYYY"
                            allowFutureDates={true}
                            showHelperText={false}
                            className={fieldClassName}
                            minDate={toDatePickerMinDate(form.policy_start_date)}
                          />
                          {errors.policy_end_date ? (
                            <p className={cardStyles.errorText}>{errors.policy_end_date}</p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Available Variables Card */}
                  <div className={cardStyles.card}>
                    <div className={cardStyles.cardHeader}>
                      <Variable className={cardStyles.cardIcon} />
                      <h3 className={cardStyles.cardTitle}>Available Variables</h3>
                    </div>
                    <div className={cardStyles.cardBody}>
                      <AvailableVariablesPanel
                        variables={TERMS_SECTION1_AVAILABLE_VARIABLES}
                        headingId="terms-s1-vars-heading"
                      />
                    </div>
                  </div>

                  {/* Document Template */}
                  <TermsDocumentTemplateBlock
                    heading="Document Template"
                    editorId="policy-template"
                    value={form.policy_template}
                    onChange={(value) => {
                      updateField('policy_template', value)
                      setEditorDirty((d) => ({ ...d, policy: true }))
                    }}
                    onSaveDraft={() => void saveDraftPolicy()}
                    onDiscardDraft={() => void discardDraft('policy_template')}
                    saveDraftDisabled={!editorDirty.policy}
                    discardDraftDisabled={!hasPolicyDraft}
                    draftSaving={draftSaving}
                    error={errors.policy_template}
                  />
                </>
              ) : (
                <>
                  {/* Section 2 Available Variables Card */}
                  <div className={cardStyles.card}>
                    <div className={cardStyles.cardHeader}>
                      <Variable className={cardStyles.cardIcon} />
                      <h3 className={cardStyles.cardTitle}>Available Variables</h3>
                    </div>
                    <div className={cardStyles.cardBody}>
                      <AvailableVariablesPanel
                        variables={TERMS_SECTION2_AVAILABLE_VARIABLES}
                        headingId="terms-s2-vars-heading"
                      />
                    </div>
                  </div>

                  {/* Section 2 Declaration Templates */}
                  <TermsDeclarationTemplatesGroup
                    declaration={{
                      editorId: 'declaration-template',
                      value: form.declaration_template,
                      onChange: (value) => {
                        updateField('declaration_template', value)
                        setEditorDirty((d) => ({ ...d, declaration: true }))
                      },
                      onSaveDraft: () => void saveDraftDeclaration(),
                      onDiscardDraft: () => void discardDraft('declaration_template'),
                      saveDraftDisabled: !editorDirty.declaration,
                      discardDraftDisabled: !hasDeclarationDraft,
                      draftSaving,
                      error: errors.declaration_template,
                    }}
                    resident={{
                      editorId: 'declaration-resident-template',
                      contentLabel: 'Declaration Resident Content',
                      value: form.declaration_resident_template,
                      onChange: (value) => {
                        updateField('declaration_resident_template', value)
                        setEditorDirty((d) => ({ ...d, resident: true }))
                      },
                      onSaveDraft: () => void saveDraftResident(),
                      onDiscardDraft: () =>
                        void discardDraft('declaration_resident_template'),
                      saveDraftDisabled: !editorDirty.resident,
                      discardDraftDisabled: !hasResidentDraft,
                      draftSaving,
                      error: errors.declaration_resident_template,
                    }}
                  />
                </>
              )}
            </div>

          <div className="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => navigate('/terms-and-conditions', { replace: true })}
                className={cn(secondaryEntityActionButtonClass, 'px-4 py-2')}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting || !isDirty}
                onClick={() => void handleSubmit()}
                className={cn(primaryEntityActionButtonClass, 'px-4 py-2')}
              >
                {submitting ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </div>

          {policyDetails?.notes && policyDetails.notes.length > 0 ? (
            <div className="p-4 sm:p-6 border-t border-slate-200">
              <div className={cardStyles.card}>
                <div className={cardStyles.cardHeader}>
                  <History className={cardStyles.cardIcon} />
                  <h3 className={cardStyles.cardTitle}>History Log</h3>
                </div>
                <div className={cardStyles.cardBody}>
                  <div className="rounded-md border border-slate-200 bg-slate-50/80">
                    <ul className="divide-y divide-slate-200">
                      {policyDetails.notes.map((row, idx) => (
                        <li key={`${row.created_at ?? ''}-${idx}`} className="flex flex-col gap-2 p-4 sm:flex-row">
                          <div className="shrink-0 border-slate-200 sm:w-48 sm:border-r sm:pr-4">
                            <p className="text-sm font-semibold text-slate-800">{row.name ?? '—'}</p>
                            <p className="text-xs text-slate-500">
                              {formatHistoryTimestamp(row.created_at)}
                            </p>
                          </div>
                          <div className="min-w-0 flex-1 text-sm text-slate-700">
                            {asTrimmedString(policyDetails.policy_id)} - {asTrimmedString(row.change_reason)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ChangeReasonModal
        isOpen={reasonOpen}
        submitting={submitting}
        onClose={() => setReasonOpen(false)}
        onSubmit={(reason) => void doSave(reason)}
      />
    </div>
  )
}
